import {
  ButtonRow,
  DATE_FORMAT_REPORT_FILENAME_DEFAULT,
  Dayjs,
  IReportEndpoint,
  isDayjs,
  makeFilename,
  SearchField,
  useSearchParamsDate,
  useSearchParamsForm,
  DateField,
  useCsvBlob,
  BaseDataGrid,
  DataGridPage,
  ActionButton,
} from "@synota-io/synota-shared-ui";
import { REPORTS } from "../constants";
import { useEffect, useMemo } from "react";
import { GetFulcrumReportInput, useReport } from "../../../api/hooks/useReport";
import dayjs from "dayjs";
import { Download } from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid";

type ReportFilters = {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  report: IReportEndpoint;
};

export const ReportsPage = () => {
  const defaultValues = {
    report: REPORTS[0],
    startDate: dayjs().startOf("month"),
    endDate: null,
  };

  const searchParamDate = useSearchParamsDate();

  const form = useSearchParamsForm<ReportFilters>(
    {
      defaultValues,
    },
    {
      endDate: searchParamDate,
      startDate: searchParamDate,
      report: {
        deserialize(value) {
          return REPORTS.find((r) => r.value === value) || REPORTS[0];
        },
        serialize(value) {
          return value?.value || "";
        },
      },
    },
  );

  const values = form.watch();

  const input: GetFulcrumReportInput = useMemo(
    () => ({
      path: values.report.path || "",
      fulcrum_secret: "",
      start_time: isDayjs(values.startDate) ? values.startDate.startOf("day").toISOString() : null,
      end_time: isDayjs(values.endDate)
        ? values.endDate.startOf("day").add(1, "day").toISOString()
        : null,
    }),
    [values],
  );

  const getFilename = makeFilename(
    values.report.label,
    isDayjs(values.startDate)
      ? values.startDate.format(DATE_FORMAT_REPORT_FILENAME_DEFAULT)
      : undefined,
    isDayjs(values.endDate)
      ? values.endDate.format(DATE_FORMAT_REPORT_FILENAME_DEFAULT)
      : undefined,
  );

  const { download, blob, isFetching } = useReport({
    filename: getFilename(),
    input,
  });

  const { columns, data, error, isLoading } = useCsvBlob({
    blob,
    hasHeaderRow: true,
  });

  useEffect(() => {
    if (error) {
      form.setError("root", error);
    }
  }, [error, form]);

  const gridColumns: GridColDef[] = useMemo(
    () =>
      columns
        ? columns.map((c) => ({
            field: c,
            type: "string",
            minWidth: 110,
            flex: 1,
          }))
        : [],
    [columns],
  );

  const rows = useMemo(
    () =>
      data?.map((d) => {
        return d.reduce((p, c, i) => (columns ? { ...p, [columns[i]]: c } : p), {
          id: JSON.stringify(d),
        });
      }) || [],
    [columns, data],
  );

  return (
    <DataGridPage
      title="Reports"
      recalculateHeightDeps={values}
      actions={
        <ActionButton
          color="primary"
          title="Download Report"
          size="small"
          disabled={!!error}
          isLoading={isFetching}
          onClick={download}
        >
          <Download fontSize="small" />
        </ActionButton>
      }
      toolbar={
        <ButtonRow direction="column">
          <ButtonRow gap={4} flexWrap="wrap" width="100%">
            <ButtonRow flexGrow={1}>
              <SearchField
                variant="filled"
                options={REPORTS}
                control={form.control}
                disableClearable
                name="report"
                label="Report Type"
                fullWidth
              />
            </ButtonRow>
            {values.report?.noFilters ? null : (
              <ButtonRow flexGrow={1}>
                <DateField
                  variant="filled"
                  clearable
                  maxDate={isDayjs(values.endDate) ? values.endDate : dayjs()}
                  control={form.control}
                  name="startDate"
                  label="From Date"
                  fullWidth
                />
                <DateField
                  variant="filled"
                  clearable
                  minDate={isDayjs(values.startDate) ? values.startDate : undefined}
                  maxDate={dayjs()}
                  control={form.control}
                  name="endDate"
                  label="To Date"
                  fullWidth
                />
              </ButtonRow>
            )}
          </ButtonRow>
        </ButtonRow>
      }
    >
      <BaseDataGrid
        key={values.report.value}
        loading={isFetching || isLoading}
        rows={rows}
        columns={gridColumns}
      />
    </DataGridPage>
  );
};
